import {
  type AskPunttAgentStep,
  type AskPunttChatAtom,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Database,
  Info,
  ListNumbers,
} from "@phosphor-icons/react";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  isMemoryCorrectionSummary,
  isResultForCall,
  memoryStatementFromSummary,
  withoutStorageLinks,
} from "./agentStepsHelpers";
import { formatToolStepBody } from "./formatToolStepBody";
import {
  STATUS_DONE_ICON_CLASS,
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";
import {
  countSubagentLogicalSteps,
  SubagentRunCardGrid,
  SubagentToolStepsForPair,
} from "./SubagentToolStep";

import { toolStepLabel } from "../../../ask-v2/rail/toolStepLabel";
import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";
import {
  collectDrawableChatAtoms,
  RENDER_COMPONENT_TOOL,
  shouldHideRenderComponentCall,
  shouldHideRenderComponentResult,
} from "../agentComponents/renderComponentAtoms";
import {
  type AskPunttArtifactContextValue,
  useAskPunttArtifactContext,
} from "../AskPunttArtifactContext";
import { useSharedNow } from "../hooks/useSharedNow";

export {
  STATUS_PANEL_ITEMS_CLASS,
  STATUS_PANEL_ITEMS_LIGHT_CLASS,
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_LIGHT_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";

/**
 * Tool names whose results land a chat-level artifact (ticket review or
 * canvas deliverable). Exported for chat.tsx's D8 streaming-suppression
 * predicate — when the in-flight tool_call is one of these, older
 * artifact rows grey out so a stale click can't yank the right pane
 * mid-stream. The clickability gate itself stays message-scoped (D7):
 * ANY tool_result inside a message that owns `viewTicketId` or a
 * non-empty `canvasContent` is eligible to be clickable, regardless of
 * whether the producing tool name is in this set.
 */
export const ARTIFACT_PRODUCING_TOOLS: ReadonlySet<string> = new Set([
  "create_ticket",
  "create_brief",
  "create_canvas",
  "create_roi",
  "create_research_brief",
]);

/**
 * The memory WRITE tool. Its chip is built from the tool CALL args, not the
 * result summary: what was remembered lives in the request. It is deliberately
 * absent from ARTIFACT_PRODUCING_TOOLS — a memory write opens no right-pane
 * artifact, so it must not grey out the ticket/canvas rows mid-stream.
 */
export const MEMORY_UPDATE_TOOL = "update_memory";

/**
 * Tools that rembrandt may still run / persist for the model, but that must
 * never render as agent-step chips (internal judgment helpers).
 */
export const HIDDEN_AGENT_STEP_TOOLS: ReadonlySet<string> = new Set([
  "craft_engagement_offer",
]);

/** Subagent spawn / skill tools — one accordion row each, never collapsed. */
export const SUBAGENT_STEP_TOOLS: ReadonlySet<string> = new Set([
  "load_capability",
  "run_subagent",
  "run_subagents",
]);

// ─── Standardized tool/skill chip ─────────────────────────────────────────────

/**
 * Size + spacing shared by every stream chip. Rows that should match the chip
 * FOOTPRINT but keep their own fill/border/icons (e.g. review-checklist steps)
 * reuse this alone, so padding/rounding/gap live in one place and never drift.
 */
export const TOOL_CHIP_SIZING = "gap-2 rounded-lg px-[11px] py-[7px]";

/**
 * Standardized stream chip footprint without panel fill/stroke — same bare
 * convention as EnterpriseImpact / FeedMemoryCard. Callers append state
 * classes (active/blocked/hover) after this base.
 */
export const TOOL_CHIP_CLASS = `flex w-full min-w-0 @container items-center ${TOOL_CHIP_SIZING} bg-transparent`;

/**
 * Primary name + optional secondary detail for tool chips.
 * Always one line: name truncates; secondary is inline when the chip is
 * ≥240px wide, otherwise an Info tooltip on the far right.
 * Class names must be complete static strings for Tailwind JIT.
 */
function ToolChipLabels({
  name,
  secondary,
  mid,
}: {
  name: string;
  secondary?: string;
  /** Compact mid content (e.g. result count) — always visible, never wraps. */
  mid?: ReactNode;
}) {
  const hasSecondary = !!secondary?.trim();
  return (
    <>
      <Text
        size="1"
        weight="regular"
        title={name}
        data-testid="agent-step-name"
        className={
          hasSecondary
            ? "min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-12 @[240px]:max-w-[45%] @[240px]:flex-none"
            : "min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-12"
        }
      >
        {name}
      </Text>
      {mid}
      {hasSecondary ? (
        <>
          <Text
            size="1"
            title={secondary}
            data-testid="agent-step-secondary-inline"
            className="hidden min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-9 @[240px]:block"
          >
            {secondary}
          </Text>
          <Tooltip content={secondary}>
            <span
              data-testid="agent-step-secondary-info"
              className="ml-auto inline-flex shrink-0 text-puntt-neutral-gray-8 @[240px]:hidden"
              aria-label={secondary}
            >
              <Info size={14} weight="regular" aria-hidden />
            </span>
          </Tooltip>
        </>
      ) : null}
    </>
  );
}

interface AgentStepsProps {
  steps: AskPunttAgentStep[];
  isStreaming: boolean;
  /**
   * The parent assistant message. When omitted (legacy callers, isolated
   * tests), artifact clickability is disabled and the component behaves
   * exactly as it did pre-feature.
   */
  message?: AskPunttMessage;
}

// ─── Step pair / group types ─────────────────────────────────────────────────

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type StepGroup = {
  tool: string;
  displayName: string;
  statusName?: string;
  pairs: StepPair[];
  /** Timestamp of first call — stable key across re-renders */
  firstTimestamp: string;
};

function runSubagentName(
  call: AskPunttAgentStep & { type: "tool_call" },
): string | null {
  if (call.tool !== "run_subagent") return null;
  const args = call.args as { name?: unknown } | undefined;
  const name = typeof args?.name === "string" ? args.name.trim() : "";
  return name || null;
}

function shouldBreakSubagentGroupAfterPair(
  group: StepGroup,
  steps: AskPunttAgentStep[],
  nextIndex: number,
): boolean {
  if (group.tool === "load_capability" || group.tool === "run_subagents") {
    return true;
  }
  if (group.tool !== "run_subagent") return false;
  const lastName = runSubagentName(
    group.pairs[group.pairs.length - 1]?.call ?? group.pairs[0].call,
  );
  const next = steps[nextIndex];
  if (next?.type !== "tool_call" || next.tool !== "run_subagent") {
    return true;
  }
  return runSubagentName(next) !== lastName;
}

function readableToolBody(
  call: AskPunttAgentStep & { type: "tool_call" },
  result?: AskPunttAgentStep & { type: "tool_result" },
): string {
  const description = call.description?.trim();
  if (description) return description;
  const summary = result?.summary?.trim();
  if (summary) {
    return formatToolStepBody(summary, call.tool);
  }
  return "";
}

/**
 * Groups consecutive same-tool call+result pairs into StepGroups.
 * Thinking/text steps are skipped (rendered as chat bubbles by ChatMessage).
 *
 * Rembrandt often converts a `tool_call` into a `tool_result` in place (same
 * array slot). Those lone results must still render as completed chips —
 * skipping them hid finished tools, and a stale unpaired `tool_call` from a
 * longer local merge left chips spinning forever after the turn settled.
 */
export function groupConsecutiveToolSteps(
  steps: AskPunttAgentStep[],
  drawableAtoms: AskPunttChatAtom[] = [],
): StepGroup[] {
  const result: StepGroup[] = [];
  let i = 0;

  while (i < steps.length) {
    const step = steps[i];

    if (step.type === "thinking" || step.type === "text") {
      i++;
      continue;
    }

    if (
      (step.type === "tool_call" || step.type === "tool_result") &&
      HIDDEN_AGENT_STEP_TOOLS.has(step.tool)
    ) {
      i++;
      continue;
    }

    if (step.type === "tool_call") {
      const callStep = step as AskPunttAgentStep & { type: "tool_call" };
      const pairedNext = steps[i + 1];
      const pairedResult =
        pairedNext?.type === "tool_result" &&
        isResultForCall(callStep, pairedNext)
          ? pairedNext
          : undefined;
      if (
        shouldHideRenderComponentCall(callStep, pairedResult, drawableAtoms)
      ) {
        i++;
        if (pairedResult) i++;
        continue;
      }

      const group: StepGroup = {
        tool: step.tool ?? "",
        displayName: step.displayName ?? "",
        statusName: step.statusName,
        pairs: [],
        firstTimestamp: step.timestamp,
      };

      while (i < steps.length) {
        const current = steps[i];
        if (current.type === "tool_call" && current.tool === step.tool) {
          const currentCall = current as AskPunttAgentStep & {
            type: "tool_call";
          };
          const pairedNext = steps[i + 1];
          const currentResult =
            pairedNext?.type === "tool_result" &&
            isResultForCall(currentCall, pairedNext)
              ? pairedNext
              : undefined;
          if (
            shouldHideRenderComponentCall(
              currentCall,
              currentResult,
              drawableAtoms,
            )
          ) {
            i++;
            if (currentResult) i++;
            continue;
          }
          const pair: StepPair = {
            call: currentCall,
          };
          i++;
          // why: close a call only with ITS OWN result. `currentResult` is
          // the adjacent tool_result already matched by `isResultForCall`
          // above (tool name, plus `stepId` when both sides carry one);
          // taking whatever tool_result happened to follow closed a
          // still-pending call with another invocation's outcome. Rembrandt
          // pre-announces a `tool_call` slot for every parallel tool_use
          // block up front and then merges each result into its own slot as
          // it lands, so `[call A pending, result B done]` is a normal
          // mid-stream array — A rendered green with B's summary and B's row
          // vanished. It also swallowed render_component results that
          // `shouldHideRenderComponentResult` must suppress.
          if (currentResult) {
            pair.result = currentResult;
            i++;
          }
          group.pairs.push(pair);
          if (shouldBreakSubagentGroupAfterPair(group, steps, i)) {
            break;
          }
        } else {
          break;
        }
      }

      if (group.pairs.length > 0) {
        result.push(group);
      }
      continue;
    }

    if (step.type === "tool_result") {
      const asResult = step as AskPunttAgentStep & { type: "tool_result" };
      if (shouldHideRenderComponentResult(asResult, drawableAtoms)) {
        i++;
        continue;
      }

      // why: rembrandt merges a landed result INTO the `tool_call` slot it
      // pre-announced for that tool_use block (`s["type"] = "tool_result"` in
      // `ask_puntt/chat.py`), so a settled turn — and EVERY reloaded thread —
      // holds one lone `tool_result` per invocation and no `tool_call` at all.
      // Pushing a group per lone result made a same-tool fan-out that rendered
      // as ONE collapsed group while it ran shatter into N full-width rows the
      // moment it finished, and come back shattered on reload.
      const group: StepGroup = {
        tool: asResult.tool ?? "",
        displayName: asResult.displayName ?? "",
        statusName: asResult.statusName,
        pairs: [],
        firstTimestamp: asResult.timestamp,
      };

      while (i < steps.length) {
        const current = steps[i];
        if (current.type !== "tool_result" || current.tool !== group.tool) {
          break;
        }
        if (shouldHideRenderComponentResult(current, drawableAtoms)) {
          i++;
          continue;
        }
        group.pairs.push({
          call: {
            ...current,
            type: "tool_call",
          } as AskPunttAgentStep & { type: "tool_call" },
          result: current,
        });
        i++;
        // Subagent rows keep one accordion each, exactly as on the tool_call
        // path — their `args.name` is dropped by the same merge, so there is
        // no name left to collapse them under.
        if (shouldBreakSubagentGroupAfterPair(group, steps, i)) break;
      }

      if (group.pairs.length > 0) {
        result.push(group);
      }
      continue;
    }

    i++;
  }

  return result;
}

function activeStreamingGroup(groups: StepGroup[]): StepGroup | undefined {
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    const lastPair = group.pairs[group.pairs.length - 1];
    if (lastPair && !lastPair.result) return group;
  }
  return groups[groups.length - 1];
}

function activeGroupStepCount(group: StepGroup): number {
  if (SUBAGENT_STEP_TOOLS.has(group.tool)) {
    return group.pairs.reduce(
      (n, pair) =>
        n +
        countSubagentLogicalSteps(
          pair.call.tool,
          pair.call.args,
          pair.result?.summary,
          pair.result?.subagentOutcomes,
        ),
      0,
    );
  }
  return group.pairs.length;
}

function streamingSummaryLabel(
  groups: StepGroup[],
  t: (key: string) => string,
  language: string,
): string {
  if (groups.length === 0) return t("agentSteps.workingFallback");

  const group = activeStreamingGroup(groups);
  if (!group) return t("agentSteps.workingFallback");
  // why: one in-flight step is already named on the row under the spinner.
  // The header says Working. Several steps in one parent group keep that
  // group's name, so the header is not Working and not one child's name.
  if (activeGroupStepCount(group) <= 1) return t("agentSteps.working");
  return toolStepLabel(group, t, language) || t("agentSteps.workingFallback");
}

// ─── Clickability derivation ─────────────────────────────────────────────────

interface Clickability {
  kind: "ticket" | "canvas";
  isActive: boolean;
  isBlocked: boolean;
  ariaLabel: string;
  testId: string;
  onActivate: () => void;
}

function messageHasCanvas(m: AskPunttMessage | undefined | null): boolean {
  const c = m?.canvasContent as { blocks?: unknown[] } | null | undefined;
  return !!(c && Array.isArray(c.blocks) && c.blocks.length > 0);
}

function formatStepTime(ts: string | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Decide whether a tool_result row maps to a re-openable artifact. Returns
 * null when no provider context exists, no parent message, or the row
 * doesn't qualify. The clickability gate is message-scoped (D7): any
 * tool_result inside a message that owns `viewTicketId` or canvas content
 * is eligible, including single-artifact chats (cowork sidebar).
 */
function deriveClickability(
  step: AskPunttAgentStep & { type: "tool_result" },
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): Clickability | null {
  if (!message || !ctx) return null;

  const reviewId = message.viewReviewId;
  const ticketId = message.viewTicketId;
  const isReviewResult =
    step.tool === "create_ticket" && !!(reviewId || ticketId);

  const canActivateV2Review =
    ctx.onActivateReview != null || (ctx.allReviewIds?.length ?? 0) > 0;

  if (isReviewResult && reviewId && canActivateV2Review) {
    const allReviewIds = ctx.allReviewIds ?? [];
    const idx = allReviewIds.indexOf(reviewId);
    const total = allReviewIds.length;
    const isActive = ctx.activeReviewId === reviewId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    const positionLabel =
      idx >= 0 ? t("agentSteps.positionLabel", { idx: idx + 1, total }) : "";
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "ticket",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.ticketAriaLabel", {
        verb,
        position: positionLabel,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-review-${reviewId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact(
            { kind: "askPunttReview", reviewId },
            "message_chip",
          );
        } else if (ctx.onActivateReview) {
          ctx.onActivateReview(reviewId);
        }
      },
    };
  }

  if (isReviewResult && ticketId) {
    const idx = ctx.allReviewTicketIds.indexOf(ticketId);
    const total = ctx.allReviewTicketIds.length;
    const isActive = ctx.activeTicketId === ticketId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    const positionLabel =
      idx >= 0 ? t("agentSteps.positionLabel", { idx: idx + 1, total }) : "";
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "ticket",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.ticketAriaLabel", {
        verb,
        position: positionLabel,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-ticket-${ticketId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact({ kind: "review", ticketId }, "message_chip");
        } else {
          ctx.onActivateTicket(ticketId);
        }
      },
    };
  }

  if (messageHasCanvas(message)) {
    const messageId = message._id;
    const isActive = ctx.activeCanvasMessageId === messageId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    // D5 fallback chain: displayName → summary → "canvas"
    const rawName =
      step.displayName || step.summary || t("agentSteps.canvasFallbackName");
    const name = rawName.length > 60 ? `${rawName.slice(0, 60)}...` : rawName;
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "canvas",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.canvasAriaLabel", {
        verb,
        name,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-canvas-${messageId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact({ kind: "canvas", messageId }, "message_chip");
        } else {
          ctx.onActivateCanvas(messageId);
        }
      },
    };
  }

  return null;
}

/**
 * Used by AgentSteps to decide whether to default-expand the steps
 * accordion (D1). True when at least one tool_result is clickable AND
 * its artifact is NOT currently active — i.e. the user could navigate
 * to it. We deliberately ignore blocked rows so an in-flight new
 * artifact doesn't force-expand the older message's accordion.
 */
function groupsHaveUnviewedClickable(
  groups: StepGroup[],
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): boolean {
  for (const g of groups) {
    for (const pair of g.pairs) {
      if (!pair.result) continue;
      const c = deriveClickability(pair.result, message, ctx, t);
      if (c && !c.isActive && !c.isBlocked) return true;
    }
  }
  return false;
}

// ─── Individual step renderers ────────────────────────────────────────────────

function GenericToolStepPair({
  pair,
  isStreaming,
}: {
  pair: StepPair;
  isStreaming: boolean;
}) {
  const { t, i18n } = useTranslation("ui");
  const call = pair.call;
  const done = !!pair.result || !isStreaming;
  const stepId = call.stepId || call.timestamp;
  const primaryName = toolStepLabel(call, t, i18n.language);
  const body = readableToolBody(call, pair.result);
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      data-testid={`agent-step-tool-${stepId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-tool-toggle-${stepId}`}
        aria-expanded={expanded}
        aria-label={primaryName}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {primaryName}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-tool-body-${stepId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-12"
            >
              {body}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CreateTicketProgressStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const { t, i18n } = useTranslation("ui");
  const startMs = useMemo(
    () => new Date(step.timestamp).getTime(),
    [step.timestamp],
  );
  // Shared ticker across all active tool chips — one interval, not N.
  const nowMs = useSharedNow(500);
  const elapsed = Math.floor((nowMs - startMs) / 1000);

  const pct = Math.min(Math.round((elapsed / 10) * 85), 85);
  const primaryName = toolStepLabel(step, t, i18n.language);

  return (
    <Flex
      direction="column"
      gap="2"
      className={TOOL_CHIP_CLASS}
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <Flex align="center" gap="2" className="w-full min-w-0">
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
        <Text
          size="1"
          title={primaryName}
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {primaryName}
        </Text>
        <Text
          size="1"
          className="ml-auto shrink-0 tabular-nums text-puntt-neutral-gray-8"
        >
          {t("agentSteps.elapsedSeconds", { n: elapsed })}
        </Text>
      </Flex>
      <div className="h-1 w-full overflow-hidden rounded-full bg-puntt-neutral-gray-3">
        <div
          className="h-full rounded-full bg-puntt-accent-7 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Flex>
  );
}

function MemoryUpdateStep({
  call,
  result,
  done,
}: {
  call: AskPunttAgentStep & { type: "tool_call" };
  /** The write's result, when one landed — the only place a settled step
   * still carries what was remembered. */
  result?: AskPunttAgentStep & { type: "tool_result" };
  /** A tool_result arrived (or the message settled): stop spinning. */
  done: boolean;
}) {
  const { t } = useTranslation("ui");
  const args = (call.args ?? {}) as {
    content?: unknown;
    corrects_node_id?: unknown;
  };
  const argContent =
    typeof args.content === "string" ? args.content.trim() : "";
  const content = argContent || memoryStatementFromSummary(result?.summary);
  const isCorrection =
    (typeof args.corrects_node_id === "string" &&
      !!args.corrects_node_id.trim()) ||
    isMemoryCorrectionSummary(result?.summary);
  const rowId = call.stepId || call.timestamp;

  const name = !done
    ? t("agentSteps.memorySavingName")
    : isCorrection
      ? t("agentSteps.memoryCorrectedName")
      : t("agentSteps.memorySavedName");
  const summary =
    content.length > 100 ? `${content.slice(0, 100)}...` : content;

  return (
    <Flex
      align="center"
      justify="between"
      gap="2"
      // why: memory chip must reuse TOOL_CHIP_CLASS — rounded-xl p-2 made it a different density from sibling rows.
      className={`${TOOL_CHIP_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      data-testid={`agent-step-memory-update-${rowId}`}
      data-step-type={done ? "tool_result" : "tool_call"}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {done ? (
          <Database
            size={16}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={16}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          data-testid="agent-step-name"
          className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-12"
        >
          {name}
        </Text>
        {summary ? (
          <Text
            size="1"
            title={summary}
            data-testid="agent-step-secondary-inline"
            className="min-w-0 truncate text-puntt-neutral-gray-12"
          >
            {summary}
          </Text>
        ) : null}
      </div>
      <CaretRight
        size={14}
        className="shrink-0 text-puntt-neutral-gray-12"
        aria-hidden
      />
    </Flex>
  );
}

function ToolResultStep({
  step,
  clickability,
}: {
  step: AskPunttAgentStep & { type: "tool_result" };
  clickability: Clickability | null;
}) {
  const { t, i18n } = useTranslation("ui");
  const formattedSummary = formatToolStepBody(step.summary, step.tool);
  const summary =
    formattedSummary.length > 100
      ? `${formattedSummary.slice(0, 100)}...`
      : formattedSummary;
  const primaryName = toolStepLabel(step, t, i18n.language);

  // D9: 200ms click flash, suppressed under prefers-reduced-motion.
  const [justClicked, setJustClicked] = useState(false);
  useEffect(() => {
    if (!justClicked) return;
    const t = setTimeout(() => setJustClicked(false), 200);
    return () => clearTimeout(t);
  }, [justClicked]);

  const resultCountMid =
    step.resultCount !== undefined && step.resultCount > 0 ? (
      <Text
        size="1"
        className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-9"
      >
        {t("agentSteps.resultCount", { count: step.resultCount })}
      </Text>
    ) : null;

  if (!clickability) {
    return (
      <Flex
        align="center"
        gap="2"
        className={TOOL_CHIP_CLASS}
        data-testid="agent-step"
        data-step-type="tool_result"
      >
        <CheckCircle
          size={14}
          weight="fill"
          className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
          aria-hidden
        />
        <ToolChipLabels
          name={primaryName}
          secondary={summary || undefined}
          mid={resultCountMid}
        />
      </Flex>
    );
  }

  const { isActive, isBlocked, ariaLabel, testId, onActivate, kind } =
    clickability;

  const handleClick = () => {
    if (isBlocked || isActive) return;
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReducedMotion) setJustClicked(true);
    onActivate();
  };

  const stateClass = isActive
    ? "border border-puntt-accent-9 bg-puntt-neutral-gray-2"
    : isBlocked
      ? "cursor-not-allowed opacity-60"
      : `cursor-pointer hover:bg-puntt-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-7 ${
          justClicked ? "bg-puntt-accent-3" : ""
        }`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBlocked}
      tabIndex={isBlocked ? -1 : 0}
      aria-disabled={isBlocked || undefined}
      aria-current={isActive ? "true" : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      data-step-type="tool_result"
      data-artifact-kind={kind}
      data-artifact-active={isActive ? "true" : "false"}
      data-artifact-blocked={isBlocked ? "true" : "false"}
      className={`${TOOL_CHIP_CLASS} text-left transition-colors ${stateClass}`}
    >
      <CheckCircle
        size={14}
        weight="fill"
        className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
        aria-hidden
      />
      <ToolChipLabels
        name={primaryName}
        secondary={summary || undefined}
        mid={resultCountMid}
      />
      {!isActive && (
        <ArrowSquareOut
          size={12}
          weight="regular"
          className={cx(
            "shrink-0 text-puntt-neutral-gray-8",
            summary ? "" : "ml-auto",
          )}
          aria-hidden
        />
      )}
    </button>
  );
}

// ─── Collapsed group row ──────────────────────────────────────────────────────

/** Settled (non-streaming) unpaired tool_call → completed chip, never spin. */
function settledResultFromCall(
  call: AskPunttAgentStep & { type: "tool_call" },
): AskPunttAgentStep & { type: "tool_result" } {
  return {
    ...call,
    type: "tool_result",
    summary: "",
    resultCount: undefined,
  };
}

function CollapsedToolGroup({
  group,
  isNew,
  message,
  ctx,
  isStreaming,
  expanded,
  onToggleExpanded,
}: {
  group: StepGroup;
  isNew: boolean;
  message: AskPunttMessage | undefined;
  ctx: AskPunttArtifactContextValue | null;
  isStreaming: boolean;
  /**
   * why: the outer accordion UNMOUNTS its items when it collapses, and it
   * collapses on its own the moment `isStreaming` flips false. Local
   * `useState` here died with that unmount, so a same-tool group the user
   * opened mid-stream came back shut when they re-opened the finished turn.
   * The open set is owned by AgentSteps (keyed by `group.firstTimestamp`) so
   * it outlives the collapse.
   */
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const { t, i18n } = useTranslation("ui");

  const callCount = group.pairs.length;
  const primaryName = toolStepLabel(group, t, i18n.language);
  const lastPair = group.pairs[group.pairs.length - 1];
  // Once the turn is settled, unpaired calls are treated as done so we never
  // leave "Update Chat Details" (etc.) spinning under a completed summary.
  // why: EVERY pair must be closed — with a parallel same-tool fan-out the
  // results land out of order, so checking only the last pair put a green
  // check on the group header while its own rows were still spinning.
  const allDone = !isStreaming || group.pairs.every((pair) => !!pair.result);

  // Build summary label: show first description/query if available
  const firstCall = group.pairs[0]?.call;
  const firstQuery = firstCall?.description
    ? firstCall.description.slice(0, 30)
    : firstCall?.tool === RENDER_COMPONENT_TOOL
      ? t("agentSteps.drawingComponent").slice(0, 30)
      : null;

  const sameNameSubagentGroup =
    group.tool === "run_subagent" && callCount > 1
      ? runSubagentName(group.pairs[0].call)
      : null;

  const label =
    sameNameSubagentGroup != null
      ? t("agentSteps.subagentGroupLabel", {
          name: sameNameSubagentGroup,
          count: callCount,
        })
      : callCount === 1
        ? primaryName
        : firstQuery
          ? t("agentSteps.groupSummaryWithQuery", {
              primaryName,
              query: firstQuery,
              count: callCount - 1,
            })
          : t("agentSteps.groupSummaryWithCount", {
              primaryName,
              count: callCount,
            });

  const renderPair = (pair: StepPair) => {
    if (SUBAGENT_STEP_TOOLS.has(pair.call.tool ?? "")) {
      return (
        <SubagentToolStepsForPair
          pair={pair}
          isStreaming={isStreaming}
          checkProgress={message?.checkProgress}
        />
      );
    }
    if (pair.call.tool === MEMORY_UPDATE_TOOL) {
      return (
        <MemoryUpdateStep
          call={pair.call}
          result={pair.result}
          done={!!pair.result || !isStreaming}
        />
      );
    }
    if (pair.result) {
      const clickability = deriveClickability(pair.result, message, ctx, t);
      if (clickability) {
        return (
          <ToolResultStep step={pair.result} clickability={clickability} />
        );
      }
      return <GenericToolStepPair pair={pair} isStreaming={isStreaming} />;
    }
    if (!isStreaming) {
      const settled = settledResultFromCall(pair.call);
      const clickability = deriveClickability(settled, message, ctx, t);
      if (clickability) {
        return <ToolResultStep step={settled} clickability={clickability} />;
      }
      return (
        <GenericToolStepPair
          pair={{ ...pair, result: settled }}
          isStreaming={isStreaming}
        />
      );
    }
    if (pair.call.tool === "create_ticket") {
      return <CreateTicketProgressStep step={pair.call} />;
    }
    return <GenericToolStepPair pair={pair} isStreaming={isStreaming} />;
  };

  if (callCount === 1) {
    const onlyPair = lastPair;
    return (
      <div
        data-testid={`agent-step-group-${group.firstTimestamp}`}
        className={isNew ? "animate-block-fade-up" : ""}
      >
        {renderPair(onlyPair)}
      </div>
    );
  }

  // Multiple same-tool pairs — collapsed summary with expand toggle
  return (
    <div
      data-testid={`agent-step-group-${group.firstTimestamp}`}
      className={cx(
        isNew ? "animate-block-fade-up" : "",
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-steps-group-toggle-${group.firstTimestamp}`}
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        // why: nested same-tool group must reuse parent accordion tokens or the inner row reads as a different density.
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {allDone ? (
          <CheckCircle
            size={14}
            weight="fill"
            className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text size="1" className="flex-1 truncate text-puntt-neutral-gray-12">
          {label}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
          expanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          data-testid={`agent-step-group-items-${group.firstTimestamp}`}
          className="flex flex-col gap-1.5 bg-transparent"
        >
          {sameNameSubagentGroup != null ? (
            <SubagentRunCardGrid
              gridId={group.firstTimestamp}
              pairs={group.pairs}
              isStreaming={isStreaming}
              checkProgress={message?.checkProgress}
            />
          ) : (
            group.pairs.map((pair) => (
              <div key={pair.call.timestamp}>{renderPair(pair)}</div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main AgentSteps component ────────────────────────────────────────────────

export function AgentSteps({
  steps: storedSteps,
  isStreaming,
  message,
}: AgentStepsProps) {
  const steps = useMemo(() => withoutStorageLinks(storedSteps), [storedSteps]);
  const { t, i18n } = useTranslation("ui");
  const ctx = useAskPunttArtifactContext();
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  // why: survives the items subtree unmounting when the outer accordion
  // collapses (which it does by itself once `isStreaming` flips false), so an
  // expanded same-tool group is still expanded when the turn is re-opened.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<
    ReadonlySet<string>
  >(() => new Set<string>());
  const prevGroupCountRef = useRef(0);

  const toggleGroupExpanded = (key: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const drawableAtoms = useMemo(
    () => collectDrawableChatAtoms(message?.components, steps),
    [message?.components, steps],
  );
  const groups = useMemo(
    () => groupConsecutiveToolSteps(steps, drawableAtoms),
    [steps, drawableAtoms],
  );

  // D1: when a message holds a clickable artifact the user hasn't viewed
  // yet, default the accordion to expanded post-stream so the affordance
  // is reachable without a manual click. Manual collapse wins (the
  // `manualToggle !== null` short-circuit) — once the user explicitly
  // toggles, we stop force-expanding.
  const hasUnviewedClickable = useMemo(
    () => groupsHaveUnviewedClickable(groups, message, ctx, t),
    [groups, message, ctx, t],
  );

  // why: `hasUnviewedClickable` goes false the instant the artifact becomes
  // active (the user clicked the chip) or blocked (a newer artifact started
  // streaming). Deriving `expanded` straight from it collapsed the accordion
  // underneath the click that opened the artifact, so the row the user just
  // activated — and its "currently viewing" state — vanished on click. Latch
  // the D1 expansion instead: only an explicit toggle closes it again.
  const d1ExpandedRef = useRef(false);
  useEffect(() => {
    if (hasUnviewedClickable) d1ExpandedRef.current = true;
  }, [hasUnviewedClickable]);

  const expanded =
    manualToggle !== null
      ? manualToggle
      : isStreaming || hasUnviewedClickable || d1ExpandedRef.current;

  const prevGroupCount = prevGroupCountRef.current;
  useEffect(() => {
    prevGroupCountRef.current = groups.length;
  }, [groups.length]);

  // Count logical tool operations (call+result pairs), not raw step rows —
  // otherwise a merged accordion of 6 tools that each have call+result shows
  // "12 Steps" instead of "6 Steps".
  // why: each subagent group re-parses its summary; `groups` is already memoized.
  const stepCount = useMemo(
    () =>
      groups.reduce((n, g) => {
        if (SUBAGENT_STEP_TOOLS.has(g.tool)) {
          return (
            n +
            g.pairs.reduce(
              (pairCount, pair) =>
                pairCount +
                countSubagentLogicalSteps(
                  pair.call.tool,
                  pair.call.args,
                  pair.result?.summary,
                  pair.result?.subagentOutcomes,
                ),
              0,
            )
          );
        }
        return n + g.pairs.length;
      }, 0),
    [groups],
  );
  const summaryText = isStreaming
    ? streamingSummaryLabel(groups, t, i18n.language)
    : t("agentSteps.stepCount", { count: stepCount });

  // Nothing to show if all steps were thinking (rendered as chat bubbles) or
  // every tool row was suppressed (e.g. in-flight render_component).
  if (groups.length === 0) return null;

  return (
    <div
      className="my-3"
      data-testid="agent-steps"
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.agentSteps)}
    >
      <div
        className={cx(
          "overflow-hidden",
          interactiveSurfaceClasses({
            expandable: true,
            muted: true,
            open: expanded,
          }),
        )}
      >
        <button
          data-testid="agent-steps-toggle"
          type="button"
          onClick={() => setManualToggle(!expanded)}
          aria-expanded={expanded}
          aria-label={t(
            expanded
              ? "agentSteps.collapseAriaLabel"
              : "agentSteps.expandAriaLabel",
          )}
          className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
        >
          {isStreaming ? (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <ListNumbers
              size={16}
              className="shrink-0 text-puntt-neutral-gray-12"
              aria-hidden
            />
          )}
          <Text
            size="1"
            weight="regular"
            className="text-puntt-neutral-gray-12"
          >
            {summaryText}
          </Text>
          <CaretDown
            size={14}
            aria-hidden
            className={`ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
          />
        </button>

        {expanded && groups.length > 0 && (
          <div
            data-testid="agent-steps-items"
            className={STATUS_PANEL_ITEMS_MUTED_CLASS}
          >
            <div className="flex flex-col gap-1.5">
              {groups.map((group, idx) => (
                <CollapsedToolGroup
                  key={group.firstTimestamp}
                  group={group}
                  isNew={idx >= prevGroupCount}
                  message={message}
                  ctx={ctx}
                  isStreaming={isStreaming}
                  expanded={expandedGroupKeys.has(group.firstTimestamp)}
                  onToggleExpanded={() =>
                    toggleGroupExpanded(group.firstTimestamp)
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
